import { FC, useCallback, useEffect, useState } from 'react';
import styles from './ReserveModal.module.css';
import { Modal } from '../Modal';
import { FormattedMessage } from 'react-intl';
import {
  reserveVehicle,
  updateReservation,
  useAvailableVehicles,
  useDateRangeValidation,
  useReserveVehicle,
  useUpdateReservation,
} from '../../services/reservations';
import { PlaceCenter } from '../PlaceCenter';
import { Spinner } from '../Spinner';
import { CarCard } from '../CarCard';
import {
  CAN_EV_DISTANCE_UNTIL_RECHARGE,
  CAN_EV_STATE_OF_CHARGE,
  FUEL_LEVEL,
  OBD_EV_DISTANCE_UNTIL_RECHARGE,
  OBD_EV_STATE_OF_CHARGE,
} from '../AssignationModal/constants';
import { Button } from '../Button';
import { DatePicker } from './DatePicker';
import clsx from 'clsx';
import { useConfirmDialog } from '../ConfirmDialogProvider';
import { noop } from 'rxjs';
import { SearchField } from '../SearchField';
import { useEditedReservation } from './service';

interface ReserveModalProps {
  open: boolean;
  onClose: () => void;
}

export const ReserveModal: FC<ReserveModalProps> = (props) => {
  const { error: validityError } = useDateRangeValidation();
  const [query, setQuery] = useState('');
  const reservation = useEditedReservation();
  const inProgress = reservation?.status === 'IN_PROGRESS';

  const { result, running } = useAvailableVehicles();
  const [selected, setSelected] = useState<number | null>(
    reservation?.objectId ?? null
  );
  const { open: confirm, close: destroy } = useConfirmDialog();

  const currentVehicleAvailable =
    !reservation || !!result?.some((car) => car.id === reservation.objectId);
  const unavailableSelected =
    !!reservation &&
    selected === reservation.objectId &&
    !currentVehicleAvailable;

  const vehicles = (result ?? [])
    .filter((car) => !inProgress || car.id === reservation?.objectId)
    .filter(
      (car) =>
        car.name.toLowerCase().indexOf(query.toLocaleLowerCase()) > -1
    );

  const onReserve = useCallback(
    () =>
      confirm(
        <FormattedMessage tagName="p" id="Vehicle.Reserve.Confirm" />
      ).then(() => reserveVehicle(selected!), noop),
    [selected, confirm]
  );

  const onUpdate = useCallback(
    () =>
      confirm(
        <FormattedMessage tagName="p" id="Vehicle.EditReserve.Confirm" />
      ).then(() => updateReservation(selected!), noop),
    [selected, confirm]
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => () => destroy(), []);

  useReserveVehicle();
  useUpdateReservation();

  return (
    <Modal
      title={
        <FormattedMessage id={reservation ? 'Reserve.Edit' : 'Reserve.Vehicle'} />
      }
      {...props}
    >
      <div className={styles.root} data-testid="reserve-dialog">
        <DatePicker startDisabled={inProgress} />
        {!inProgress && (
          <SearchField
            fullWidth
            id="search-vehicle"
            onChange={(e) => setQuery(e.target.value)}
            value={query}
            autoComplete="off"
          />
        )}
        <div className={styles.list}>
          {running && (
            <PlaceCenter>
              <Spinner />
            </PlaceCenter>
          )}
          {!running && (
            <>
              {validityError ? (
                <div className={clsx(styles.error, 'light')}>
                  <FormattedMessage id={validityError} />
                </div>
              ) : (
                <>
                  {reservation && !currentVehicleAvailable && (
                    <>
                      <div
                        className={clsx(styles.error, 'light')}
                        data-testid="vehicle-unavailable-error"
                      >
                        <FormattedMessage id="Reserve.Vehicle.NotAvailable" />
                      </div>
                      <CarCard
                        className={styles.unavailable}
                        vehicleName={reservation.objectName}
                        onClick={() => setSelected(reservation.objectId)}
                        active={selected === reservation.objectId}
                        fuelLevel=""
                        stateOfCharge=""
                        distanceUntilRecharge=""
                      />
                    </>
                  )}
                  {!vehicles.length && currentVehicleAvailable && (
                    <span className="light">
                      <FormattedMessage id="Reserve.Vehicles.Empty" />
                    </span>
                  )}
                  {vehicles.map((car) => (
                    <CarCard
                      key={car.id}
                      vehicleName={car.name}
                      driverName={car.firstDriver?.fullName}
                      onClick={() => setSelected(car.id)}
                      active={!!selected && selected === car.id}
                      fuelLevel={car.inputs[FUEL_LEVEL]}
                      stateOfCharge={
                        car.inputs[CAN_EV_STATE_OF_CHARGE] ??
                        car.inputs[OBD_EV_STATE_OF_CHARGE]
                      }
                      distanceUntilRecharge={
                        car.inputs[CAN_EV_DISTANCE_UNTIL_RECHARGE] ??
                        car.inputs[OBD_EV_DISTANCE_UNTIL_RECHARGE]
                      }
                    />
                  ))}
                </>
              )}
            </>
          )}
        </div>
        {!!selected && (
          <Button
            onClick={reservation ? onUpdate : onReserve}
            className={styles.button}
            disabled={running || !!validityError || unavailableSelected}
            data-testid="reserve-submit"
          >
            <FormattedMessage id={reservation ? 'Save' : 'Reserve'} />
          </Button>
        )}
      </div>
    </Modal>
  );
};
